/* buildISLinesForCompany — Phase 2a chart-of-accounts builder.

   Produces the per-company IS_LINES array from:
   - gl_line_mappings rows (leaf lines, carrying role/subsection/sort_order)
   - SYNTHETIC_LINES.alwaysPresent (headers, totals, calcs, margins + oi_interest)
   - SYNTHETIC_LINES.moduleGated (D&A entries; revenue entries are data-driven)
   - revenue_module_mappings rows (Session 0): synthetic revenue lines for
     module instances whose gl_line_key is NOT already in glMappings

   Output shape matches the current hardcoded IS_LINES in computeIS.js exactly
   (same fields in same order per line), so Phase 2b can swap in this builder
   output without changing the engines. No engines consume this builder yet;
   Phase 2a only validates equivalence for Semitek.

   Inputs
   - companyId: integer (reserved for future per-tenant customization)
   - glMappings: rows from gl_line_mappings with role/subsection/sort_order
   - revModuleMappings: rows from revenue_module_mappings — Session 0 consumes
     this to drive synthetic revenue line emission (replacing the static
     rev_ae_quota / rev_company_quota entries previously in moduleGated)
   - moduleContext: { aeQuotaEnabled, companyQuotaEnabled, hasFixedAssets, hasIntangibleAssets }
*/

import { SYNTHETIC_LINES, ROLE_TO_SOURCE, CONDITIONAL_ROLES, LINE_ROLES, CATEGORY, displayGroupLabel, IS_CATEGORY_DISPLAY_TO_SLUG } from './lineRoles';
import { buildPayrollIsLineKey, buildDgSortBases, buildDgLabels, dgSortBase, PAYTYPE_SORT_OFFSET } from './payrollKeys';
import { indexGlByLeafKey, ownedLabelFor } from '../utils/leafLabels';
import { implSyntheticKey, isRoutedElsewhere } from '../utils/implKeys';
import { miscLeafKey, isUnroutedMisc } from '../utils/miscKeys';
import { DA_STREAMS, daRouteTarget } from './daRouting';
import { groupIdOf, templateGroupLabels } from '../utils/seatDepartments';

// Session 0: per-module-type config for synthetic IS revenue line emission.
// sortOrder values match the static moduleGated entries previously in
// SYNTHETIC_LINES.moduleGated to preserve byte-identity for Semitek/Chris Test.
const MODULE_TYPE_TO_IS_CONFIG = {
  ae_quota:      { sortOrder: 80,  defaultLabel: 'Account Executive Quota Revenue' },
  company_quota: { sortOrder: 90,  defaultLabel: 'Company Quota Revenue' },
  // Path C Session 2a: misc_revenue rows emit as standalone revenue leaves.
  // No global enabled flag in moduleContext — emission is gated solely on
  // the row's presence in revenue_module_mappings (see moduleEnabledForIS).
  // Multiple misc_revenue rows all share sortOrder=100 and tie-break by
  // input order from the DB query (CLAUDE.md caps at 10 misc lines/tenant).
  misc_revenue:  { sortOrder: 100, defaultLabel: 'Misc Revenue' },
};

// Session 3b: dynPayrollLeaves per-paytype constants.
const PAYTYPE_TO_LABEL = {
  wages:         'Salaries',
  bonuses:       'Bonuses',
  commissions:   'Commissions',   // commission ship 2c (R13): only for a qualifying (category, group)
  payroll_taxes: 'Payroll Taxes',
  benefits:      'Benefits',
};
const PAYTYPE_TO_ROLE = {
  wages:         LINE_ROLES.PAYROLL_WAGES,
  bonuses:       LINE_ROLES.PAYROLL_BONUSES,
  commissions:   LINE_ROLES.PAYROLL_COMMISSIONS,
  payroll_taxes: LINE_ROLES.PAYROLL_TAX,
  benefits:      LINE_ROLES.PAYROLL_BENEFITS,
};
// Step-3 pass: PAYTYPE_SORT_OFFSET and the per-display_group sort bases now live
// in payrollKeys.js — shared with the onboarding-time classifier (inferIsMeta) so
// the order onboarding WRITES and the order the engine EMITS come from one source.
//
// The bases were a hardcoded map (client_success:1000 … sales_mktg:4000) that
// literally named AE Model's four slugs; every other tenant's groups collapsed to a
// single DEFAULT band, tying them and leaving their order arbitrary. buildDgSortBases
// derives the bands from the tenant's OWN department_mappings order instead. AE Model
// derives to exactly 1000/2000/3000/4000 — byte-identical.

function moduleEnabledForIS(ctx, moduleType) {
  if (moduleType === 'ae_quota')      return !!ctx.aeQuotaEnabled;
  if (moduleType === 'company_quota') return !!ctx.companyQuotaEnabled;
  // Path C Session 2a: misc_revenue has no global toggle on moduleContext
  // — each row is its own line item. Emit the leaf whenever a misc_revenue
  // row exists in revenue_module_mappings. The leaf's FORECAST is driven by a
  // misc_revenue_lines row routed to this gl_line_key via target_gl_line_key
  // (Session 5e-2, Path A) — $0 forecast until such a line is created + routed
  // (5f). (Functional: always emit; routing only changes WHERE the forecast lands.)
  if (moduleType === 'misc_revenue')  return true;
  return false;
}

// Session 3a: deptMappings parameter accepted but not consumed. Session 3b
// will use it to drive dynPayrollLeaves emission (parallel to dynRevLeaves).
//
// Sub-commit 1.6: isActuals is an optional 6th param (defrev_data.is_actuals
// JSONB blob) used to decouple HISTORICAL revenue leaf emission from the
// forecast-module enabled-state gate. A revenue_module_mappings row that
// would otherwise be dropped (module_type='skip' or owning module disabled)
// still emits a leaf if its gl_line_key carries non-zero historical values
// in is_actuals — so historical revenue display is independent of forecast
// configuration. Optional with empty-object default keeps legacy callers
// (server/verify-is-lines-semitek.js, etc.) syntactically valid.
/* ★ Build B — THE LAYOUT HALF AND THE VALUES HALF, SPLIT.
 *
 * buildISLinesForCompany did two separable jobs in one body: work out WHICH LEAVES a tenant has
 * (from gl_line_mappings, revenue_module_mappings, department_mappings, moduleContext and
 * is_actuals), then ASSEMBLE them into the ordered statement skeleton. The second job computes no
 * values and reads nothing but the leaves and the department labels — so it can be reused by any
 * caller that has leaves of its own.
 *
 * That is the point of the split: the Step-4 review screen cannot run the leaf half (it has no
 * department_mappings until Step 5c, and its payroll rows carry role NULL by design), but it CAN
 * supply leaves it derived itself and get the model's own ordering, nesting, sub-totals and labels
 * back. One layout implementation, two callers — instead of the review screen keeping a parallel
 * copy of the section order.
 *
 * NOTHING about the ordering, the labels or the emission logic changes here. The two functions
 * contain the original body verbatim, cut at the point where leaf-building ends and assembly
 * begins; buildISLinesForCompany is now their composition and keeps its exact signature.
 */
export function buildISLeaves(glMappings, revModuleMappings, deptMappings, moduleContext, isActuals, implementationLines, miscRevenueLines) {
  const ctx = moduleContext || {};
  const acts = isActuals || {};


  // Sub-section header labels, operator-set per group (department_mappings.
  // display_group_label). Absent → displayGroupLabel(slug) below, i.e. the hardcoded
  // DISPLAY_GROUP_LABELS map + titlecase. Once slugs are derived-and-hidden, a slug like
  // 'sales_and_marketing' would titlecase to "Sales And Marketing" — so the header has to
  // be able to come from data, not from the slug.
  /* 2026-10-28: plus the TEMPLATE's own text for a group no department owns (SubBase's "Marketing"), never a title-cased
     slug -- templateGroupLabels (utils/seatDepartments). {} for a company whose groups are stored as slugs. A department's own
     label wins. */
  const dgLabels = {
    ...templateGroupLabels((glMappings || []).filter(r => r.statement_type === 'IS' && r.role).map(r => r.display_group), deptMappings),
    ...buildDgLabels(deptMappings),
  };


  // Normalize GL-mapped leaves. Level is deliberately omitted — emitSection
  // fills it from the subsection's leafLevel so OIE leaves stay at level 1.
  //
  // Path C Session 2a: dedupe by is_line_key. Multiple gl_line_mappings rows can
  // share an is_line_key (AE Model had 11 such collapsed keys pre-Session-4 re-keying
  // — e.g. ga_vendors had 17 rows). The lowest-id row wins.
  //
  // ★ Step-3 pass: the tiebreaker was (sort_order ASC, id) — which made sort_order
  // the ONLY thing on this file that is not display-only. The dedupe winner supplies
  // the leaf's display_name, role and subsection, and role selects `source` via
  // ROLE_TO_SOURCE — so re-ordering two rows that shared a key could have re-routed an
  // engine. That made "reordering is safe" a claim about the data (no duplicate keys
  // exist today) rather than a property of the code. Keying the dedupe on `id` alone
  // makes sort_order display-only BY CONSTRUCTION, which is what licenses the reorder
  // UI. Byte-identical: all three tenants have zero duplicate is_line_keys, so both
  // tiebreakers pick the same winner (asserted in verify-step3-glm-capture.js GATE 5).
  //
  // Leaf ORDER is unaffected: the per-subsection bucket sort below is a stable sort on
  // sort_order, so equal-sort_order leaves keep this array's order — which was already
  // id order under the old comparator (equal sort_order ⇒ id tiebreak) and is id order
  // now.
  const sortedGlMappings = [...(glMappings || [])].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  const seenKeys = new Set();
  const glLeaves = sortedGlMappings
    .filter(r => r.statement_type === 'IS' && r.role)
    .filter(r => {
      if (seenKeys.has(r.is_line_key)) return false;
      seenKeys.add(r.is_line_key);
      return true;
    })
    .map(r => ({
      id: r.is_line_key,
      label: r.display_name,
      level: undefined,
      source: ROLE_TO_SOURCE[r.role],
      conditional: CONDITIONAL_ROLES.has(r.role) || undefined,
      role: r.role,
      subsection: r.subsection,
      sort_order: r.sort_order,
      display_group: groupIdOf(r.display_group) ?? null,   // 2026-10-28: a template group TEXT is the group deptSlug(text)
    }));

  // Synthetic alwaysPresent leaves (those that declare a subsection, e.g. oi_interest)
  //
  /* ★ THE SWEEP-INTEREST SEAM. `oi_interest` was the ONE alwaysPresent leaf that never
   * adopted the dynRevLeaves rule below (`if (glLineKeySet.has(row.gl_line_key)) continue;`
   * — a forecast module's output books onto the OPERATOR'S historical leaf and the parallel
   * synthetic is not emitted). It could not: the historical line is keyed oi_interest_income
   * and the synthetic oi_interest, so the two ids NEVER collide and an id test can never
   * fire. MEASURED false on all four tenants (diffs/interest-seam-phase0b.txt §2.3).
   *
   * ⛔ THE DISCRIMINATOR IS THE STORED OPERATOR ANSWER, NOT A ROLE SCAN. Suppressing on
   * `some(l => l.role === 'oi_interest')` would route off inferIsMeta's NAME test
   * (/interest/), i.e. an accounting decision taken from a GL line's name — the thing §E1
   * forbids and B5 s5 removed from the asset side. The role lookup lives ONLY in the Step-9
   * picker's PRE-SELECTION; the engine reads what the operator confirmed.
   *
   * The key must still name a leaf we actually EMIT — otherwise we would drop the synthetic
   * and put nothing in its place, and the forecast interest would render nowhere.
   * `undefined` (every diagnostic caller, and every tenant until an operator confirms)
   * suppresses nothing, so this is byte-identical by construction until the answer exists. */
  const sweepInterestKey = ctx.sweepInterestLineKey || null;
  const glLeafIdSet = new Set(glLeaves.map(l => l.id));
  const suppressOiInterest = !!sweepInterestKey &&
    sweepInterestKey !== 'oi_interest' &&        // the sentinel = "keep a separate forecast line"
    glLeafIdSet.has(sweepInterestKey);
  const synAlwaysLeaves = SYNTHETIC_LINES.alwaysPresent
    .filter(s => s.subsection)
    .filter(s => !(s.id === 'oi_interest' && suppressOiInterest))
    .map(s => ({
      id: s.id,
      label: s.label,
      level: s.level,
      source: s.source,
      conditional: s.conditional || undefined,
      role: s.role,
      subsection: s.subsection,
      sort_order: s.sort_order,
    }));

  // Synthetic module-gated leaves (post-Session-0: D&A only — other_depreciation,
  // other_amortization). Revenue moduleGated entries are now data-driven below.
  /* D&A routing, Part 2: a stream whose stored target names an emitted leaf (and is not its
     sentinel) books its forecast onto that leaf, so its synthetic is suppressed -- per stream,
     through the one three-state predicate in daRouting.js. null / the sentinel / an
     unemitted key keep the synthetic, so an unanswered tenant is byte-identical. */
  const daSuppressed = new Set(DA_STREAMS
    .filter(st => daRouteTarget(ctx[st.ctxKey], st.synthetic, glLeafIdSet) !== st.synthetic)
    .map(st => st.synthetic));
  const synGatedLeaves = SYNTHETIC_LINES.moduleGated
    .filter(s => s.enabledCheck(ctx))
    .filter(s => !daSuppressed.has(s.id))
    .map(s => ({
      id: s.id,
      label: s.label,
      level: s.level,
      source: s.source,
      conditional: s.conditional || undefined,
      role: s.role,
      subsection: s.subsection,
      sort_order: s.sort_order,
    }));

  // Session 0: data-driven module-revenue synthetic emission.
  // For each revenue_module_mappings row whose gl_line_key is NOT already
  // a leaf in glMappings (i.e., no historical revenue line for the module),
  // emit a synthetic IS line. Replaces the static rev_ae_quota /
  // rev_company_quota entries that previously lived in SYNTHETIC_LINES.moduleGated.
  // Semitek/Chris Test: gl_line_keys 'rev_ae_quota' / 'rev_company_quota' are
  //   not in their glMappings → synthetics emit (byte-identical to pre-Session-0)
  // AE Model: gl_line_keys 'rev_enterprise_revenue' / 'rev_self_serve' / etc.
  //   ARE in glMappings (historical lines) → no synthetic, historical line used
  //
  // Sub-commit 1.6: historical-display decouple. A row whose forecast gate
  // would drop it (module_type='skip' → no cfg, or owning module disabled)
  // still emits a leaf when its gl_line_key carries non-zero historical
  // values in is_actuals. Forecast months render $0 until an engine drives
  // them; historical months render the is_actuals overlay. Sort fallback
  // for skip-type rows is 200 (after the configured-module slots 80/90/100).
  const hasHistoricalRevenueData = (key) => {
    const series = acts[key];
    if (!series) return false;
    for (const v of Object.values(series)) {
      if (v !== 0) return true;
    }
    return false;
  };
  const glLineKeySet = new Set(glLeaves.map(l => l.id));
  /* Built ONCE for the dynRev label lookup below — the same index utils/leafLabels.js
   * uses for the balance sheet, so "which glm row owns this leaf key" has one answer. */
  const glOwnerByLeafKey = indexGlByLeafKey(glMappings);
  const dynRevLeaves = [];
  // ★ ORDER-INDEPENDENCE (same class as the dept-band regression; caught by GATE 9).
  // Revenue leaves take sort_order from MODULE_TYPE_TO_IS_CONFIG, so rows of the same module
  // type TIE — and skip-type rows all tie at 200. leavesBySub's sort is stable, so a tie is
  // broken by THIS array's order, which is whatever the caller's SELECT returned (production
  // orders by gl_line_key; the gate ordered by id). Same rows, different statement.
  // Sorting the input here makes the tie deterministic for every caller. gl_line_key is the
  // tiebreak because it is what production already renders, so nothing visibly moves.
  // (A real operator-set revenue order needs revenue_module_mappings.sort_order — a separate
  // follow-up build. This fixes the ORDER-DEPENDENCE, not the ordering feature.)
  const sortedRmm = [...(revModuleMappings || [])].sort((a, b) =>
    String(a.gl_line_key ?? '').localeCompare(String(b.gl_line_key ?? '')));
  for (const row of sortedRmm) {
    if (!row.gl_line_key) continue;
    if (glLineKeySet.has(row.gl_line_key)) continue;
    const cfg = MODULE_TYPE_TO_IS_CONFIG[row.module_type];
    const forecastEnabled = !!cfg && moduleEnabledForIS(ctx, row.module_type);
    const hasHistorical = hasHistoricalRevenueData(row.gl_line_key);
    if (!forecastEnabled && !hasHistorical) continue;
    /* ★ THE LABEL COMES FROM ITS OWNER. This read `row.display_name` — the
     * revenue_module_mappings row — which is a CACHE of gl_line_mappings.display_name and
     * is measurably stale: the Step-7 loader seeds it from `prettify(<the slug>)`, so
     * "Discounts/Refunds Given" comes back as "Discountsrefunds Given" (the slug rule
     * deletes '/'). The mapped-IS path a hundred lines above already reads the source
     * (`label: r.display_name` off the glm row); this makes the two halves of ONE function
     * agree about where a label lives.
     * ⛔ Matched BY KEY (the stamped leaf_key), never by name.
     * ⓘ The rmm/defaultLabel chain is KEPT as the fallback: a revenue line with no glm row
     *   is a real mid-wizard state, and it should keep the label it has rather than blank. */
    const ownedLabel = ownedLabelFor(glOwnerByLeafKey, row.gl_line_key);
    dynRevLeaves.push({
      id: row.gl_line_key,
      label: ownedLabel || row.display_name || cfg?.defaultLabel || row.gl_line_key,
      level: 2,
      source: 'payroll',
      role: LINE_ROLES.REVENUE_LINE,
      subsection: CATEGORY.REVENUE,
      sort_order: cfg?.sortOrder ?? 200,
    });
  }

  // Session 3b: dynPayrollLeaves emission from department_mappings. For each
  // (catKey, displayGroup) pair, emit 4 leaves (wages, bonuses, payroll_taxes,
  // benefits). Commissions are folded into bonuses at the engine level —
  // no separate commissions leaf for the per-dg path.
  //
  // Skip-if-in-glLineKeySet (matches dynRevLeaves precedent): for collapsed
  // display_groups (dg === catKey → middle-drop produces a catch-all key like
  // 'cogs_wages'), glLeaves already has the key → dynPayroll skips emission
  // (glLeaves wins, no duplication). For multi-dg display_groups (AE Model
  // S&M client_success/rev_ops/data_science/sales_mktg), keys are distinct
  // from catch-alls → dynPayroll emits → multi-dg path in emitSection fires.
  //
  // emittedPayrollKeys dedupes the Sales+Marketing case (both depts share
  // display_group='sales_mktg' → same key tuple → emit once).
  const dynPayrollLeaves = [];
  const emittedPayrollKeys = new Set();
  const dgBases = buildDgSortBases(deptMappings, IS_CATEGORY_DISPLAY_TO_SLUG);
  /* Commission ship 2c (R13, Brian 10-09 Q4 A / N4 (a)): a 5th leaf, Commissions, ONLY for the (category, group) pairs in
   * moduleContext.commissionLineKeys -- utils/perDgPayrollActuals commissionLineKeys, the ONE qualifying set (a payroll row
   * stored as 'commissions'). Every other group: four leaves, commission folded into Bonuses, exactly as before. */
  const commissionLineKeys = new Set((moduleContext && moduleContext.commissionLineKeys) || []);
  for (const dm of (deptMappings || [])) {
    const catKey = IS_CATEGORY_DISPLAY_TO_SLUG[dm.is_category];
    if (!catKey) continue;
    const dg = dm.display_group || catKey;
    const dgBase = dgSortBase(dgBases, catKey, dg);
    for (const paytype of ['wages', 'bonuses', 'commissions', 'payroll_taxes', 'benefits']) {
      const key = buildPayrollIsLineKey(catKey, dg, paytype);
      if (paytype === 'commissions' && !commissionLineKeys.has(key)) continue;   // 2c: not a qualifying group -> folded
      if (emittedPayrollKeys.has(key)) continue;
      if (glLineKeySet.has(key)) continue;
      emittedPayrollKeys.add(key);
      dynPayrollLeaves.push({
        id: key,
        label: PAYTYPE_TO_LABEL[paytype],
        level: 2,
        source: 'payroll',
        conditional: (paytype === 'bonuses' || paytype === 'commissions') ? true : undefined,
        role: PAYTYPE_TO_ROLE[paytype],
        subsection: catKey,
        sort_order: dgBase + PAYTYPE_SORT_OFFSET[paytype],
        display_group: dg,
      });
    }
  }

  /* ★ PART C — THE IMPLEMENTATION LEAF, EMITTED BY THE BUILDER.
   *
   * Until now buildISLines emitted NO rev_impl_* leaf at all: the money reached isData
   * and total_revenue (computeIS:24 folds the ids into its sumOf), but the statement
   * SKELETON had no row for it, and IncomeStatementPage spliced its own at render time.
   * Measured: a forged tenant with one implementation line produced 73 leaves here and
   * 74 with a misc line — the builder simply had no implementation branch.
   *
   * ⛔ A `MODULE_TYPE_TO_IS_CONFIG` ENTRY IS NOT THE FIX, AND THAT IS WHY THIS LOOP IS
   *   SEPARATE. That map drives dynRevLeaves, which is keyed on revenue_module_mappings
   *   ROWS — one per gl_line_key. A tenant has N implementation LINES
   *   (implementation_lines is UNIQUE(company_id, source_type, source_id)) and at most
   *   ONE rmm row of module_type 'implementation', so an rmm-keyed emission can never
   *   produce a leaf per line.
   *
   * ★ POSITION: sort_order 300+ puts these LAST among revenue leaves, immediately before
   *   total_revenue — reproducing exactly where the page splice put them. Measured on
   *   cid=8: ae_quota 80 · company_quota 90 · misc_revenue 100 · skip-type rmm rows 200.
   * ★ ORDER-INDEPENDENCE (GATE 9's rule: a derivation SORTS ITS OWN INPUT): sorted by id
   *   rather than trusting the caller's array order, so two callers handing the same rows
   *   in different orders cannot produce different statements. The retired splice used
   *   raw array order.
   * ⓘ The enabled filter matches the splice exactly (`l.enabled`), so a disabled line
   *   emits nothing and cannot add an empty row.
   * ⓘ Routing is NOT this part's business: the key is the synthetic rev_impl_<id> that
   *   every consumer already spells. When Part B adds a target column, THIS is the one
   *   place the resolution belongs — and because the leaf now comes from here, a routed
   *   line will not also produce a second, empty row. */
  /* ★★ PART B — THE ROUTED LINE EMITS NO SYNTHETIC LEAF, and this is the half that stops
   *   routing from producing a DUPLICATE, NOW-EMPTY ROW. A ROUTED implementation line appears
   *   ONCE, at its target — which is an EXISTING glLeaf and therefore already emitted above —
   *   so emitting `rev_impl_<id>` as well would add a phantom carrying $0 forever, beside the
   *   real row carrying the money. That is precisely the outcome Part C existed to prevent.
   * ★ AND THIS IS THE PLACE Part C NAMED for it — see its note above: "When Part B adds a
   *   target column, THIS is the one place the resolution belongs."
   *
   * ⚠⚠ ★ 2026-09-15 — THE PREDICATE WAS TWO-STATE ON A THREE-STATE DESIGN. It read
   *     `l && l.enabled && !l.target_gl_line_key`
   *   and the KEEP-A-SEPARATE-LINE SENTINEL IS A NON-NULL VALUE (`rev_impl_<id>`), so
   *   `!l.target_gl_line_key` was FALSE and the leaf was suppressed AS IF THE LINE WERE
   *   ROUTED — while `runFullModel:809` and `orchestrator:154` both deposited the money at
   *   exactly that key. Measured on cid=36's real line 11: the money was computed, deposited,
   *   and reached NO ROW AND NO TOTAL.
   * ⛔ THE ROOT WAS THE MIRRORING THIS COMMENT USED TO CLAIM AS A VIRTUE. It said the filter
   *   "MIRRORS THE MISC PRECEDENT CHARACTER-FOR-CHARACTER: IncomeStatementPage:105 filters
   *   `l.enabled && !l.target_gl_line_key` … in exactly these words." That predicate is
   *   CORRECT FOR MISC — misc is TWO-STATE (NULL or a routed key; its control writes `''` and
   *   the server normalises it to NULL, measured). IMPLEMENTATION IS THREE-STATE, and the
   *   copy brought a two-state question into a place that needed a three-state one.
   *   ⇒ ★ A CHARACTER-FOR-CHARACTER MIRROR IS ONLY SAFE WHEN THE TWO SUBJECTS HAVE THE SAME
   *     SHAPE. Misc has no sentinel; ⛔ do not re-mirror this predicate back onto misc.
   * ★★ THE QUESTION THIS FILTER MUST ASK IS "is the target something OTHER than this row's own
   *   synthetic?" — ⛔ never "is the target NULL?". NULL (unanswered) and the sentinel
   *   (deliberately separate) BOTH emit; only a genuinely different key suppresses. That is
   *   Part D's stated design, which sites 1, 2 and 4 already honoured.
   * ⓘ THE COMPARISON BELOW IS THE ONE THE CODEBASE ALREADY MAKES, verbatim in shape:
   *   RevenueBuildPage's `implTargetedByOthers` (:1789) `l.target_gl_line_key !== \`rev_impl_${l.id}\``,
   *   `implRouteState` (:1795), and server.js's 409 check (:2309) and validateImplTarget hatch
   *   (:1435). ⛔ NOT a fifth spelling invented here — the same test, negated for this use.
   * ⓘ AND THE IS/BS ASYMMETRY THIS RESOLVES: the BS/CFS emission (orchestrator:221) keys on
   *   `recognition_mode === 'deferred'` and NEVER reads the routing column, so the deferred
   *   liability formed correctly in every state while the IS row did not. The BS was right. */
  const implLeaves = [...(implementationLines || [])]
    .filter(l => l && l.enabled && !isRoutedElsewhere(l.id, l.target_gl_line_key))
    .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
    .map((l, i) => ({
      id: implSyntheticKey(l.id),
      label: l.name,
      level: 2,
      source: 'implementation',
      conditional: true,
      role: LINE_ROLES.REVENUE_LINE,
      subsection: CATEGORY.REVENUE,
      sort_order: 300 + i,
    }));

  /* W-089 (2026-10-01) -- THE UNROUTED MISC LEAF, EMITTED BY THE BUILDER (the implementation leaf above is the precedent).
   * Until now an unrouted misc line (target_gl_line_key NULL -- "Create new IS line") had NO leaf here: computeIS added
   * rev_misc_<id> to Total Revenue at run time and IncomeStatementPage spliced its row, so the skeleton -- the one layout
   * owner -- was short by the whole line and pre-flight P16 (which reads the skeleton) blocked a correct model.
   * Now: one leaf per enabled unrouted line, role revenue_line (Brian, 10-01: the BS DSO base picker offers it like any
   * revenue line), source 'misc' (P4 asks history only of source 'revenue'), sorted by id (GATE 9's rule).
   * POSITION: sort_order 400+ puts it LAST in the revenue section, after the implementation leaves (300+), immediately
   * before total_revenue -- where the retired splice put it. Revenue module leaves use 80-200 (MODULE_TYPE_TO_IS_CONFIG).
   * A ROUTED line emits nothing: its money lands on its target, an existing leaf. computeIS's own summer is deleted in the
   * same commit (left in, the line would be counted twice). */
  const miscLeaves = [...(miscRevenueLines || [])]
    .filter(isUnroutedMisc)
    .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
    .map((l, i) => ({
      id: miscLeafKey(l),
      label: l.name,
      level: 2,
      source: 'misc',
      conditional: true,
      role: LINE_ROLES.REVENUE_LINE,
      subsection: CATEGORY.REVENUE,
      sort_order: 400 + i,
    }));

  // Bucket and sort leaves by subsection
  const leavesBySub = {};
  for (const leaf of [...glLeaves, ...synAlwaysLeaves, ...synGatedLeaves, ...dynRevLeaves, ...dynPayrollLeaves, ...implLeaves, ...miscLeaves]) {
    (leavesBySub[leaf.subsection] = leavesBySub[leaf.subsection] || []).push(leaf);
  }
  for (const sub of Object.keys(leavesBySub)) {
    leavesBySub[sub].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }

  return { leavesBySub, dgLabels };
}

/* ★ Build B — the ASSEMBLY half: leaves in, ordered skeleton out.
 *
 * Pure with respect to its two inputs and computes no values. Every layout decision lives here:
 * the section walk order, the alwaysPresent header/total/calc/margin templates, the multi-
 * department sub-header/sub-total path, the orphan fold, and the Option-γ drop of empty
 * subsections.
 */
export function assembleISSkeleton({ leavesBySub, dgLabels }) {
  // Index alwaysPresent templates by id for O(1) lookup during walk
  const alwaysById = Object.fromEntries(SYNTHETIC_LINES.alwaysPresent.map(s => [s.id, s]));

  // Session 2b: track which total IDs were actually emitted as the skeleton
  // walks. Used by emitById('total_sga') to filter sumOfTotals down to the
  // emitted set — supports Option γ (sw/pf/tme drop on tenants with no leaves
  // in those subsections). For Semitek/Chris Test all 7 SG&A totals emit
  // (sw/pf/tme via gl_line_mappings rows; ooe via moduleGated D&A) → filter
  // is a no-op → byte-identical sumOf array.
  const emittedTotals = new Set();
  const out = [];

  const emitLeaf = (leaf, leafLevel) => {
    const entry = { id: leaf.id, label: leaf.label, level: leaf.level ?? leafLevel };
    if (leaf.source) entry.source = leaf.source;
    if (leaf.conditional) entry.conditional = true;
    if (leaf.role) entry.role = leaf.role;
    out.push(entry);
  };

  const emitSection = ({ headerId, subsection, totalId, leafLevel = 2 }) => {
    if (headerId) {
      const h = alwaysById[headerId];
      out.push({ id: h.id, label: h.label, level: h.level, type: 'header' });
    }
    const leaves = leavesBySub[subsection] || [];

    // Session 2b: compute distinct non-null display_groups in first-appearance
    // order (within the already sort_order-sorted leaves array). Scaffolding-
    // only — until Sessions 3+4 land dynPayrollLeaves carrying display_group,
    // all leaves have display_group=null and the multi-dg path never fires.
    const dgsInOrder = [];
    const seenDg = new Set();
    for (const leaf of leaves) {
      const dg = leaf.display_group;
      if (dg != null && !seenDg.has(dg)) {
        seenDg.add(dg);
        dgsInOrder.push(dg);
      }
    }
    const multiDg = dgsInOrder.length >= 2;

    if (!multiDg) {
      // ── Flat path (current behavior; byte-identical for Semitek/Chris Test) ──
      for (const leaf of leaves) emitLeaf(leaf, leafLevel);
      if (totalId) {
        const tpl = alwaysById[totalId];
        const entry = { id: tpl.id, label: tpl.label, level: tpl.level, type: 'total', sumOf: leaves.map(l => l.id) };
        if (tpl.role) entry.role = tpl.role;
        out.push(entry);
        emittedTotals.add(totalId);
      }
      return;
    }

    // ── Multi-dg path (latent until Sessions 3+4) ───────────────────────────
    // Partition leaves by display_group; emit per-group header + leaves +
    // total in first-appearance order, then a section total summing the
    // sub-totals. NULL-display_group leaves inside this branch are a
    // defensive fallback (Path A2 guarantees they won't appear in Sessions
    // 2b/3/4) — render them at leafLevel+1 without a sub-section header and
    // surface a console.warn.
    const leavesByDg = {};
    const orphanedLeaves = [];
    for (const leaf of leaves) {
      if (leaf.display_group == null) {
        orphanedLeaves.push(leaf);
      } else {
        (leavesByDg[leaf.display_group] = leavesByDg[leaf.display_group] || []).push(leaf);
      }
    }
    if (orphanedLeaves.length > 0) {
      console.warn(
        `[buildISLines] subsection='${subsection}' has ${orphanedLeaves.length} ` +
        `leaf(s) with display_group=null inside a multi-dg subsection. ` +
        `Rendering them flat at level ${leafLevel + 1} without a sub-section header. ` +
        `Leaf ids: ${orphanedLeaves.map(l => l.id).join(', ')}`
      );
      for (const leaf of orphanedLeaves) emitLeaf(leaf, leafLevel + 1);
    }
    // ★★ ORPHANS MUST BE SUMMED. The section total below sums the dg SUB-TOTALS
    // (subTotalIds) — an orphan leaf (display_group=null) joins no dg group, so without
    // this it would RENDER on the statement and be EXCLUDED from its own section total,
    // and thence from Total SG&A / Operating Income / Net Income. That silent exclusion
    // FIRED LIVE: cid=3's 6 S&M vendor rows were orphaned pre-Session-4f and $3,949,068.64
    // of historical spend was rendered-but-uncounted in total_sm until 4f set their
    // display_group by accident (verified against the pre-4f glm state). We FOLD the
    // orphan leaves into the section total's sumOf directly — a VISIBLE ROW MUST COUNT.
    // Folding, not suppressing: silently dropping a line is the B3-1 pattern this whole
    // catalog exists to prevent. The console.warn stays (fail-safe AND fail-loud).
    const orphanIds = orphanedLeaves.map(l => l.id);
    const subTotalIds = [];
    for (const dg of dgsInOrder) {
      const dgLeaves = leavesByDg[dg];
      const dgLabel    = dgLabels[dg] || displayGroupLabel(dg);
      const dgHeaderId = `${subsection}_${dg}_header`;
      const dgTotalId  = `${subsection}_${dg}_total`;
      out.push({ id: dgHeaderId, label: dgLabel, level: leafLevel, type: 'header' });
      for (const leaf of dgLeaves) emitLeaf(leaf, leafLevel + 1);
      out.push({
        id: dgTotalId,
        label: `Total ${dgLabel}`,
        level: leafLevel,
        type: 'total',
        sumOf: dgLeaves.map(l => l.id),
      });
      subTotalIds.push(dgTotalId);
    }
    if (totalId) {
      const tpl = alwaysById[totalId];
      // Orphans first so the section total = every dg sub-total PLUS every orphan leaf.
      // (Byte-identical for every live tenant: no tenant has an orphan — cid=3 has zero
      // post-4f, cid=1/cid=2 have no department_mappings so they take the flat path and
      // never reach here. orphanIds is [] and this is exactly subTotalIds.)
      const entry = { id: tpl.id, label: tpl.label, level: tpl.level, type: 'total', sumOf: [...orphanIds, ...subTotalIds] };
      if (tpl.role) entry.role = tpl.role;
      out.push(entry);
      emittedTotals.add(totalId);
    }
  };

  const emitById = (id) => {
    const t = alwaysById[id];
    if (t.type === 'header') {
      out.push({ id: t.id, label: t.label, level: t.level, type: 'header' });
    } else if (t.type === 'total') {
      // Only reachable for totals that sum other totals (total_sga).
      // Session 2b: filter sumOfTotals through emittedTotals so dropped
      // sw/pf/tme subsections (Option γ) don't leave stale references in
      // sumOf. For Semitek/Chris Test all 7 SG&A totals emit → filter is a
      // no-op → output sumOf equals the original sumOfTotals array
      // (byte-identical).
      const candidates = t.sumOfTotals || [];
      const filtered = candidates.filter(refId => emittedTotals.has(refId));
      const entry = { id: t.id, label: t.label, level: t.level, type: 'total', sumOf: filtered };
      if (t.role) entry.role = t.role;
      out.push(entry);
      emittedTotals.add(t.id);
    } else if (t.type === 'calc') {
      const entry = { id: t.id, label: t.label, level: t.level, type: 'calc', calc: t.calc };
      if (t.role) entry.role = t.role;
      out.push(entry);
    } else if (t.type === 'margin') {
      const entry = { id: t.id, label: t.label, level: t.level, type: 'margin', numId: t.numId, denId: t.denId };
      if (t.role) entry.role = t.role;
      out.push(entry);
    }
  };

  // Walk the IS skeleton — mirrors the hardcoded IS_LINES order 1:1
  emitSection({ headerId: 'rev_header',  subsection: 'revenue', totalId: 'total_revenue' });
  emitSection({ headerId: 'cogs_header', subsection: 'cogs',    totalId: 'total_cogs' });
  emitById('gross_profit');
  emitById('gp_margin');
  emitById('sga_header');
  emitSection({ headerId: 'sm_header',  subsection: 'sm',  totalId: 'total_sm' });
  // Session 2b Option γ: emit sw/pf/tme only when tenant has leaves in those
  // subsections. Semitek/Chris Test have rows (1/4/2 respectively → byte-
  // identity preserved); AE Model has none (6 header/total entries drop,
  // total_sga.sumOf filter handles the dangling sumOfTotals refs).
  if (leavesBySub.sw?.length)  emitSection({ headerId: 'sw_header',  subsection: 'sw',  totalId: 'total_sw' });
  if (leavesBySub.pf?.length)  emitSection({ headerId: 'pf_header',  subsection: 'pf',  totalId: 'total_pf' });
  if (leavesBySub.tme?.length) emitSection({ headerId: 'tme_header', subsection: 'tme', totalId: 'total_tme' });
  emitSection({ headerId: 'ga_header',  subsection: 'ga',  totalId: 'total_ga' });
  emitSection({ headerId: 'rd_header',  subsection: 'rd',  totalId: 'total_rd' });
  emitSection({ headerId: 'ooe_header', subsection: 'ooe', totalId: 'total_ooe' });
  emitById('total_sga');
  emitById('operating_income');
  emitById('ebit_margin');
  emitSection({ headerId: 'oie_header', subsection: 'oie', totalId: 'total_oie', leafLevel: 1 });
  emitById('net_income');
  emitById('ebitda');

  return out;
}

/* Unchanged signature and behaviour — now the composition of the two halves above.
 * (companyId has always been unused in the body; it is kept so every existing caller and
 * verifier keeps working.) */
// eslint-disable-next-line no-unused-vars
export function buildISLinesForCompany(companyId, glMappings, revModuleMappings, deptMappings, moduleContext, isActuals, implementationLines, miscRevenueLines) {
  return assembleISSkeleton(buildISLeaves(glMappings, revModuleMappings, deptMappings, moduleContext, isActuals, implementationLines, miscRevenueLines));
}
